import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { translate } from '../Services/translation';
import Pagination from './Pagination';

const ORDER_DATE = 'orderDate';
const EXTERNAL_ORDER_ID = 'externalId';
const CUSTOMER_INFO_LAST_NAME = 'customer.lastName';
const ASCENDING = 'ascending';
const DESCENDING = 'descending';

const SORT_CONFIG_STORAGE = 'sortConfigStorage';
const SEARCH_CONFIG_STORAGE = 'searchConfigStorage';
const PAGINATION_CONFIG_STORAGE = 'paginationConfigStorage';

const useSortableData = (items, config) => {
  const [sortConfig, setSortConfig] = React.useState(config);

  const getValueByPath = (value, obj) =>
    value
      ? value.split('.').reduce((o, i) => (o ? o[i] : undefined), obj)
      : undefined;

  const sortedItems = React.useMemo(() => {
    let sortableItems = [...items];
    if (sortConfig != null) {
      sortableItems.sort((a, b) => {
        const valueA = getValueByPath(sortConfig.key, a);
        const valueB = getValueByPath(sortConfig.key, b);
        if (valueA < valueB) {
          return sortConfig.direction === ASCENDING ? -1 : 1;
        }
        if (valueA > valueB) {
          return sortConfig.direction === ASCENDING ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [items, sortConfig]);

  const requestSort = (key) => {
    let direction = ASCENDING;
    if (sortConfig && sortConfig.key === key) {
      if (!sortConfig.direction) {
        direction = ASCENDING;
      } else if (sortConfig.direction === DESCENDING) {
        direction = ASCENDING;
      } else if (sortConfig.direction === ASCENDING) {
        direction = DESCENDING;
      }
    }
    setSortConfig({
      key,
      direction,
    });
  };
  if (sortConfig != null) {
    localStorage.setItem(SORT_CONFIG_STORAGE, JSON.stringify(sortConfig));
  }
  return {
    items: sortedItems,
    requestSort,
    sortConfig,
  };
};

const useSearchableData = (items, config) => {
  const [searchConfig, setSearchConfig] = React.useState(config);

  const filteredItems = React.useMemo(() => {
    let filteringItems = [...items];
    if (searchConfig != null) {
      filteringItems = filteringItems.filter((item) => {
        return (
          (searchConfig.status ? item.status === searchConfig.status : true) &&
          (searchConfig.orderId != null && searchConfig.orderId !== ''
            ? item.externalId
              ? item.externalId
                  .toLowerCase()
                  .includes(searchConfig.orderId.toLowerCase())
              : false
            : true)
        );
      });
    }
    return filteringItems;
  }, [items, searchConfig]);

  const requestSearch = (searchData) => {
    setSearchConfig({
      ...searchData,
    });
  };
  if (searchConfig != null) {
    localStorage.setItem(SEARCH_CONFIG_STORAGE, JSON.stringify(searchConfig));
  }
  return {
    filteredItems,
    requestSearch,
    searchConfig,
  };
};

const usePaginationData = (items, config) => {
  const [paginationConfig, setPaginationConfig] = React.useState(config);

  const paginationData = React.useMemo(() => {
    let filteringItems = [...items];
    if (paginationConfig != null) {
      const { currentPage, totalPages, pageLimit } = paginationConfig;

      const offset = (currentPage - 1) * pageLimit;
      filteringItems = filteringItems.slice(offset, offset + pageLimit);

      return {
        currentPage,
        currentItems: filteringItems,
        totalPages,
      };
    }
    return {
      currentPage: 1,
      filteringItems,
      totalPages: 1,
    };
  }, [items, paginationConfig]);

  const onPageChanged = (data) => {
    setPaginationConfig({
      ...data,
    });
  };

  if (paginationConfig != null) {
    localStorage.setItem(
      PAGINATION_CONFIG_STORAGE,
      JSON.stringify(paginationConfig)
    );
  }
  return {
    ...paginationData,
    onPageChanged,
    paginationConfig,
  };
};

const ListResellerOrder = (props) => {
  const { orders, resellerOrderLink } = props;
  const statusList = orders
    ? [...new Set(orders.map((item) => item.status))]
    : [];

  if (location.hash !== '#comeback') {
    localStorage.removeItem(SORT_CONFIG_STORAGE);
    localStorage.removeItem(SEARCH_CONFIG_STORAGE);
    localStorage.removeItem(PAGINATION_CONFIG_STORAGE);
  }
  const sortingConfigStorage = localStorage.getItem(SORT_CONFIG_STORAGE);
  let initialSortingConfig =
    sortingConfigStorage && sortingConfigStorage != 'undefined'
      ? JSON.parse(sortingConfigStorage)
      : undefined;
  const {
    items,
    requestSort,
    sortConfig = initialSortingConfig,
  } = useSortableData(
    orders.map((item) => {
      return {
        ...item,
        orderDate: item.orderDate ? new Date(item.orderDate) : undefined,
      };
    }),
    initialSortingConfig
  );

  const searchConfigStorage = localStorage.getItem(SEARCH_CONFIG_STORAGE);
  let initialSearchConfig =
    searchConfigStorage && searchConfigStorage != 'undefined'
      ? JSON.parse(searchConfigStorage)
      : undefined;
  const {
    filteredItems,
    requestSearch,
    searchConfig = initialSearchConfig,
  } = useSearchableData(items, initialSearchConfig);

  const paginationConfigStorage = localStorage.getItem(PAGINATION_CONFIG_STORAGE);
  const initialPaginationConfig =
    paginationConfigStorage && paginationConfigStorage != 'undefined'
      ? JSON.parse(paginationConfigStorage)
      : {
          pageLimit: 30,
          currentPage: 1,
        };
  const {
    currentItems,
    onPageChanged,
    paginationConfig = initialPaginationConfig,
  } = usePaginationData(filteredItems, initialPaginationConfig);

  useEffect(() => {
    if (
      JSON.stringify(sortConfig) != JSON.stringify(initialSortingConfig) ||
      JSON.stringify(searchConfig) != JSON.stringify(initialSearchConfig)
    ) {
      initialSortingConfig = sortConfig
        ? {
            ...sortConfig,
          }
        : undefined;
      initialSearchConfig = searchConfig
        ? {
            ...searchConfig,
          }
        : undefined;
      onPageChanged({
        currentPage: 1,
        totalPages: filteredItems ? filteredItems.length : 0,
        pageLimit: paginationConfig.pageLimit,
      });
    }
  }, [sortConfig, searchConfig]);

  const getClassNamesFor = (name) => {
    if (!sortConfig) {
      return '';
    }
    return sortConfig.key === name ? sortConfig.direction : '';
  };

  const statusSelect = (
    <div className="custom-select">
      <select
        value={searchConfig ? searchConfig.status : ''}
        onChange={(event) =>
          requestSearch({
            ...searchConfig,
            status: event.target.value,
          })
        }
      >
        <option key="0" value="">
          {translate('orderlist.column.status')}
        </option>
        {statusList.map((item, index) => (
          <option key={index + 1} value={item}>
            {item}
          </option>
        ))}
      </select>
    </div>
  );
  return (
    <div>
      <div className="align-center row">
        <div className="columns small-3 order__search-block">
          <input
            type="text"
            className="order__search-field"
            placeholder={translate('orderlist.search_by_ordernumber')}
            value={searchConfig ? searchConfig.orderId : ''}
            onChange={(event) =>
              requestSearch({
                ...searchConfig,
                orderId: event.target.value,
              })
            }
          />
          <i className="order__search"></i>
        </div>
        <div className="columns small-3">{statusSelect}</div>
      </div>
      <div id="reseller-order-history" className="column">
        {currentItems && currentItems.length > 0 ? (
          <div id="reseller-order-history__wrapper" className="simple-table">
            <div className="row medium-unstack no-margin simple-table__header">
              <div
                className={
                  'columns small-4 medium-2 large-2 reseller-order-history__header-cell ' +
                  getClassNamesFor(ORDER_DATE)
                }
                onClick={() => requestSort(ORDER_DATE)}
              >
                {translate('orderlist.column.orderdate')}
              </div>
              <div
                className={
                  'columns small-4 medium-2 large-2 reseller-order-history__header-cell ' +
                  getClassNamesFor(EXTERNAL_ORDER_ID)
                }
                onClick={() => requestSort(EXTERNAL_ORDER_ID)}
              >
                {translate('orderlist.column.ordernumber')}
              </div>
              <div
                className={
                  'columns small-4 medium-4 large-4 reseller-order-history__header-cell ' +
                  getClassNamesFor(CUSTOMER_INFO_LAST_NAME)
                }
                onClick={() => requestSort(CUSTOMER_INFO_LAST_NAME)}
              >
                {translate('orderlist.column.ordercustomer')}
              </div>
              <div className="columns hide-for-small-only hide-for-medium-only large-2 ">
                {translate('orderlist.column.ordersum')}
              </div>
              <div className="columns hide-for-small-only hide-for-medium-only large-2 ">
                {translate('orderlist.column.status')}
              </div>
            </div>
            {currentItems &&
              currentItems.map((order, index) => {
                let orderDate = order.orderDate;
                if (orderDate) {
                  let date = orderDate.getDate().toString();
                  date = date.length > 1 ? date : '0' + date;
                  let month = (orderDate.getMonth() + 1).toString();
                  month = month.length > 1 ? month : '0' + month;
                  orderDate = `${date}.${month}.${orderDate.getFullYear()}`;
                }
                return (
                  <div
                    key={index}
                    className="order__row medium-unstack no-margin"
                  >
                    <a
                      href={`${resellerOrderLink.href}?id=${order.id}`}
                      className="order__row-link"
                    >
                      <div className="order__date columns small-4 medium-2 large-2">
                        {orderDate}
                      </div>
                      <div className="order__id columns small-4 medium-2 large-2">
                        {order.externalId}
                      </div>
                      <div className="order__products columns small-4 medium-4 large-4">
                        {order.customer
                          ? order.customer.firstName +
                            ' ' +
                            order.customer.lastName
                          : null}
                      </div>

                      <div className="columns order__total large-2">
                        {order.grandTotal}
                      </div>

                      <div className="columns order__status large-2">
                        {order.status}
                      </div>

                      <div className="columns small-4 medium-8 order__total-status-wrapper-mobile">
                        <div>{order.status}</div>
                        <div>{order.grandTotal}</div>
                      </div>
                    </a>
                  </div>
                );
              })}
          </div>
        ) : (
          <div className="order-no-order align-center">
            {translate('orderlist.noorderfound')}
          </div>
        )}
      </div>

      <Pagination
        totalRecords={filteredItems.length}
        pageLimit={paginationConfig.pageLimit}
        pageNeighbours={1}
        currentPage={paginationConfig.currentPage}
        onPageChanged={onPageChanged}
      />
    </div>
  );
};

export default ListResellerOrder;

ListResellerOrder.propTypes = {
  orders: PropTypes.array,
  resellerOrderLink: PropTypes.object,
};
